import { useCallback, useEffect } from 'react';
import ChatInput from './ChatInput';

/**
 * チャット入力エリアコンポーネント
 */
export default function ChatInputArea({
    input,
    setInput,
    onSubmit,
    loading,
    purging,
    isTurnLimitReached,
    maxTurns,
    textareaRef,
    formRef,
    isWsConnected,
    openingFetching,
    sendDisabled = false
}) {
    // Enter で送信（Shift+Enter で改行、IME変換中は無視）
    const handleTextareaKeyDown = useCallback((e) => {
        if (e.key !== "Enter") return;
        if (e.isComposing || e.nativeEvent?.isComposing) return;
        if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
        e.preventDefault();
        if (loading || purging || openingFetching || sendDisabled) return;
        if (!input.trim()) return;
        const form = formRef.current;
        if (form) {
            try {
                form.requestSubmit();
            } catch (_) {
                form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
            }
        }
    }, [loading, purging, openingFetching, sendDisabled, input, formRef]);

    // テキストエリアの自動リサイズ
    const autoResizeTextarea = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        const cs = window.getComputedStyle(el);
        const lineHeightStr = cs.lineHeight;
        const fontSize = parseFloat(cs.fontSize || "16");
        const lineHeight =
            lineHeightStr === "normal"
                ? Math.round(fontSize * 1.4)
                : parseFloat(lineHeightStr || "22");
        const paddingTop = parseFloat(cs.paddingTop || "0");
        const paddingBottom = parseFloat(cs.paddingBottom || "0");
        const borderTop = parseFloat(cs.borderTopWidth || "0");
        const borderBottom = parseFloat(cs.borderBottomWidth || "0");
        const verticalChrome = paddingTop + paddingBottom + borderTop + borderBottom;
        const maxPx = Math.floor(lineHeight * 3 + verticalChrome);
        const next = Math.min(el.scrollHeight, maxPx);
        el.style.height = `${next}px`;
        // 入力末尾が見えるようにスクロール
        el.scrollTop = el.scrollHeight;
    }, [textareaRef]);

    // 入力値が変更されたときに自動リサイズ
    useEffect(() => {
        autoResizeTextarea();
    }, [input, autoResizeTextarea]);

    return (
        <ChatInput
            input={input}
            onInputChange={setInput}
            onSubmit={onSubmit}
            onKeyDown={handleTextareaKeyDown}
            loading={loading}
            purging={purging}
            isTurnLimitReached={isTurnLimitReached}
            maxTurns={maxTurns}
            textareaRef={textareaRef}
            formRef={formRef}
            isWsConnected={isWsConnected}
            openingFetching={openingFetching}
            sendDisabled={sendDisabled}
        />
    );
}

